import {
  IDashboardCardFilters,
  IDashboardCardInstance,
  IDashboardDataQuery,
  IDashboardDataRequest,
  IReportCriteriaFiltersSchemaValues,
  normalizeToArrayValue,
  TDashboardCardFilterKey,
  trimSeconds,
} from "@ryde/shared";

import { IDashboardCard } from "../dynamic-dashboard.models";
import {
  isLegacyCard,
  pickCardSupportedAppliedFilters,
} from "../dynamic-dashboard.system-cards.utils";
import { TCalendarDates } from "./dashboard-card-data.models";
import { mergeCardFilters } from "./dashboard-card-data.utils";
import { LEGACY_CARD_NAMES } from "./dashboard-fetch-query.constants";

const buildDateRange = (calendarDates: TCalendarDates): IDashboardDataQuery => {
  if (!calendarDates) {
    return {};
  }

  // trimSeconds returns undefined for an invalid date; if either bound is bad we
  // send no range and let the backend fall back to its default window.
  const from = trimSeconds(calendarDates.date1);
  const to = trimSeconds(calendarDates.date2);
  if (!from || !to) {
    return {};
  }

  return { dateRange: { from, to } };
};

const toCardFilters = ({
  card,
  appliedFilters,
}: {
  card: IDashboardCard;
  appliedFilters: IReportCriteriaFiltersSchemaValues;
}): IDashboardCardFilters | undefined => {
  const merged = mergeCardFilters({
    appliedFilters: pickCardSupportedAppliedFilters({
      cardName: card.legacyCardName,
      filters: appliedFilters,
    }),
    cardFilters: card.cardFilters,
  });

  const filters: IDashboardCardFilters = {};
  Object.entries(merged).forEach(([key, value]) => {
    const ids = normalizeToArrayValue(value)
      .map(Number)
      .filter(id => Number.isFinite(id));

    if (ids.length) {
      filters[key as TDashboardCardFilterKey] = ids;
    }
  });

  return Object.keys(filters).length ? filters : undefined;
};

/**
 * The Legacy cards as INSTANCES, each with its own id and filters. This is what
 * keeps two cards of the same type independent: a name-keyed request can only
 * carry one filter set per card type, so the second card would silently overwrite
 * the first and both would render the same data.
 */
const getLegacyCardInstances = ({
  cards,
  appliedFilters,
}: {
  cards: IDashboardCard[];
  appliedFilters: IReportCriteriaFiltersSchemaValues;
}): IDashboardCardInstance[] =>
  cards.flatMap(card =>
    isLegacyCard(card)
      ? [
          {
            id: card.id,
            cardName: card.legacyCardName,
            filters: toCardFilters({ card, appliedFilters }),
          },
        ]
      : [],
  );

export const buildDashboardFetchQuery = ({
  calendarDates,
  cards,
  appliedFilters = {},
}: {
  calendarDates: TCalendarDates;
  cards: IDashboardCard[];
  appliedFilters?: IReportCriteriaFiltersSchemaValues;
}): IDashboardDataRequest => ({
  ...buildDateRange(calendarDates),
  cardNames: LEGACY_CARD_NAMES,
  cards: getLegacyCardInstances({ cards, appliedFilters }),
});
